import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/erp/ui";
import { Users, Factory, ListChecks, UserCheck, TrendingUp } from "lucide-react";

const SALES_COLORS = { NEW: "bg-slate-400", "UP COMING": "bg-emerald-500", REVIEWING: "bg-amber-400", QUOTED: "bg-sky-400", APPROVED: "bg-emerald-500", REJECTED: "bg-rose-400", COMPLETED: "bg-army" };

export default function VirtualOffice() {
  const [d, setD] = useState(null);
  useEffect(() => {
    const fetchData = () => api.get("/virtual-office").then(({ data }) => setD(data)).catch(() => {});
    fetchData();
    const t = setInterval(fetchData, 15000);
    return () => clearInterval(t);
  }, []);
  if (!d) return <div className="text-slate-400 font-mono text-sm">Loading…</div>;

  const maxFunnel = Math.max(1, ...Object.values(d.funnel || {}));
  const Stat = ({ icon: Icon, label, value, color }) => (
    <Card className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}><Icon className="w-5 h-5 text-white" /></div>
      <div><div className="text-2xl font-head font-bold">{value}</div><div className="text-xs text-slate-500">{label}</div></div>
    </Card>
  );

  return (
    <div>
      <PageHeader title="Virtual Office" subtitle="Pantau kerja tim, produksi & antrian secara real-time." />
      <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Factory} label="WO Aktif" value={d.active_count} color="bg-sky-500" />
        <Stat icon={ListChecks} label="Antrian / Tertunda" value={d.queue_count} color="bg-amber-500" />
        <Stat icon={UserCheck} label="Hadir Hari Ini" value={d.present} color="bg-emerald-500" />
        <Stat icon={TrendingUp} label="Sales Approved" value={d.sales?.APPROVED || 0} color="bg-army" />
      </div>

      {/* Office floor — animated team desks */}
      <Card className="p-6 mt-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Users className="w-4 h-4 text-sky-600" /> Lantai Kerja Tim</h3>
        {d.team.length === 0 ? <p className="text-sm text-slate-400">Belum ada tim yang sedang mengerjakan stage.</p> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 relative">
            {d.team.map((t, i) => (
              <div key={i} data-testid={`office-desk-${i}`} className="relative bg-white border border-slate-200 rounded-xl p-4 anim-up" style={{ animationDelay: `${i * 60}ms` }}>
                <span className="absolute top-3 right-3 flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                </span>
                <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center font-semibold text-sm">{(t.name || "?")[0]}</div>
                <div className="mt-2 text-sm font-medium truncate">{t.name}</div>
                <div className="text-xs text-slate-500">mengerjakan <span className="text-sky-600 font-medium">{t.task}</span></div>
                <div className="text-[11px] font-mono text-slate-400 mt-1">{t.wo} · {t.customer || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card className="p-6">
          <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Factory className="w-4 h-4 text-sky-600" /> Produksi Berjalan</h3>
          <div className="space-y-2.5">
            {Object.keys(d.funnel).length === 0 && <p className="text-sm text-slate-400">Belum ada produksi aktif.</p>}
            {Object.entries(d.funnel).map(([s, n]) => (
              <div key={s} className="flex items-center gap-3">
                <span className="w-24 text-xs font-mono text-slate-500 truncate">{s}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden"><div className="bg-sky-500 h-full rounded-full transition-all duration-700" style={{ width: `${(n / maxFunnel) * 100}%` }} /></div>
                <span className="w-6 text-right text-sm font-medium">{n}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><ListChecks className="w-4 h-4 text-amber-600" /> Antrian / Pekerjaan Tertunda</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {d.queue.length === 0 ? <p className="text-sm text-slate-400">Tidak ada antrian.</p> :
              d.queue.map((q, i) => (
                <div key={i} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm" data-testid={`queue-item-${i}`}>
                  <span className="font-mono text-xs">{q.code}</span><span className="flex-1 px-3 truncate">{q.customer || "—"}</span><Badge>{q.stage}</Badge>
                </div>
              ))}
          </div>
        </Card>
      </div>

      <Card className="p-6 mt-6">
        <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-army" /> Pipeline Tim Sales</h3>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {Object.entries(d.sales).map(([k, v]) => (
            <div key={k} data-testid={`sales-${k}`} className="text-center rounded-xl border border-slate-200 p-3">
              <div className={`w-full h-1.5 rounded-full ${SALES_COLORS[k] || "bg-slate-300"} mb-2`} />
              <div className="text-xl font-head font-bold">{v}</div>
              <div className="text-[10px] font-mono text-slate-500">{k}</div>
            </div>
          ))}
        </div>
        <div className="mt-5 border-t border-slate-100 pt-4">
          <div className="text-xs font-mono text-slate-500 mb-2">PERFORMA PER SALES {d.sales_target ? `· target ${formatIDR(d.sales_target)}` : ""}</div>
          {(d.sales_people || []).length === 0 ? <p className="text-sm text-slate-400">Belum ada data sales.</p> : (
            <div className="space-y-2">
              {d.sales_people.map((p, i) => {
                const pct = d.sales_target ? Math.min(100, Math.round((p.revenue / d.sales_target) * 100)) : 0;
                return (
                  <div key={i} data-testid={`sales-person-${i}`} className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-full bg-army/10 text-army flex items-center justify-center text-xs font-semibold shrink-0">{(p.name || "?")[0].toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-sm"><span className="truncate">{p.name}</span><span className="font-medium">{formatIDR(p.revenue)}</span></div>
                      <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden"><div className="h-full bg-army rounded-full transition-all duration-700" style={{ width: `${d.sales_target ? pct : Math.min(100, p.approved * 20)}%` }} /></div>
                      <div className="text-[10px] font-mono text-slate-400 mt-0.5">{p.quotes} quote · {p.approved} approved{d.sales_target ? ` · ${pct}% target` : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="mt-4 space-y-2">
          {d.recent_sales.map((q) => (
            <div key={q.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
              <span className="font-medium">{q.customer_name}</span>
              <span className="flex items-center gap-2"><span className="font-mono text-xs text-slate-400">{q.code}</span><span>{formatIDR(q.quote?.total || 0)}</span><Badge>{q.status}</Badge></span>
            </div>
          ))}
        </div>
      </Card>
      </>
    </div>
  );
}
